import g from '#core/global';
import load from '#core/load';


const svg_$ = 'svg';
const icon_draw_$ = '.icon[data-draw]';
const drawable_elements_$ = 'path, line, polyline, polygon, circle, rect, ellipse';

const initialized_attr = 'data-draw-initialized';
const delay_attr = 'data-draw-delay';
const duration_attr = 'data-draw-duration';
const reverse_direction_attr = 'data-draw-reverse-direction';
const reverse_order_attr = 'data-draw-reverse-order';
const proportional_attr = 'data-draw-proportional';

const drawing_ready_c = 'icon--drawing-ready';
const svg_drawing_c = 'svg--drawing';
const svg_drawn_c = 'svg--drawn';
const icon_drawing_c = 'icon--drawing';
const icon_drawn_c = 'icon--drawn';

const path_length_prop = '--path-length';
const path_index_prop = '--path-index';
const path_offset_initial_prop = '--path-offset-initial';
const path_offset_final_prop = '--path-offset-final';
const duration_prop = '--draw-duration';
const stroke_dash_offset_prop = 'stroke-dashoffset';

const draw_complete_e = 'draw-complete';

const root_margin = '50px';
const draw_duration_min = 300;

let has_icons = false;
let observer = null;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('icon')) return false;
		
		load.content(() => {
			// Small delay to ensure CSS is applied and elements are rendered
			setTimeout(() => {
				initialize_icons_in_scope();
				setup_mutation_observer();
				has_icons = true;
			}, 50);
		});
		load.all(() => {
			initialize_icons_in_scope();
		});
	},
	
	
	/**
	 * Initialize icons in a specific scope
	 * @param {element} $scope
	 */
	update($scope = null) {
		initialize_icons_in_scope($scope);
	},
	
	
	/**
	 * Manually trigger drawing animation
	 * @param {element} $icon
	 */
	draw($icon) {
		animate_paths($icon);
	},
	
};

export default module;
module.init();


/**
 * Set up a single icon for drawing animation
 * @param {element} $icon
 */
function setup_icon($icon) {
	if ($icon.hasAttribute(initialized_attr)) return;
	
	setup_paths($icon);
	get_observer().observe($icon);
	$icon.setAttribute(initialized_attr, g.true_s);
}


/**
 * Get or create intersection observer
 * @return {IntersectionObserver}
 */
function get_observer() {
	if (!observer) {
		observer = new IntersectionObserver(function(entries) {
			entries.forEach(function(entry) {
				if (entry.isIntersecting) {
					animate_paths(entry.target);
					observer.unobserve(entry.target);
				}
			});
		}, { rootMargin: root_margin });
	}
	return observer;
}


/**
 * Initialize icons in a scope
 * @param {element} $scope
 */
function initialize_icons_in_scope($scope = null) {
	$scope = $scope || document;
	const $icons = $scope.querySelectorAll(icon_draw_$);
	$icons.forEach(setup_icon);
}


/**
 * Set up MutationObserver to watch for AJAX-loaded content
 */
function setup_mutation_observer() {
	const mutation_observer = new MutationObserver(function(mutations) {
		mutations.forEach(function(mutation) {
			mutation.addedNodes.forEach(function(node) {
				if (node.nodeType === Node.ELEMENT_NODE) {
					// Check if the added node itself is a drawable icon
					if (node.matches && node.matches(icon_draw_$)) {
						setup_icon(node);
					}
					// Check for drawable icons within the added node
					const $icons = node.querySelectorAll && node.querySelectorAll(icon_draw_$);
					if ($icons) {
						$icons.forEach(setup_icon);
					}
				}
			});
		});
	});
	
	mutation_observer.observe(document.body, {
		childList: true,
		subtree: true
	});
}


/**
 * Set up SVG paths for drawing animation
 * @param {element} $icon
 */
function setup_paths($icon) {
	const $svg = $icon.querySelector(svg_$);
	if (!$svg) return;
	
	// Immediately add the drawing class to prevent initial flash
	$icon.classList.add(drawing_ready_c);
	
	const $drawable_elements = get_drawable_elements($svg);
	if (!$drawable_elements.length) return;
	
	const is_reverse_direction = $icon.getAttribute(reverse_direction_attr) === g.true_s;
	
	$drawable_elements.forEach(($element, index) => {
		const length = get_element_length($element);
		if (!length) return false;
		
		$element.style.setProperty(path_length_prop, length);
		$element.style.setProperty(path_index_prop, index);
		
		// Set initial and final offset based on reverse direction mode
		$element.style.setProperty(
			path_offset_initial_prop,
			length * (is_reverse_direction ? -1 : 1)
		);
		$element.style.setProperty(path_offset_final_prop, '0');
	});
}


/**
 * Get the length of an SVG element
 * @param {element} $element
 * @return {number}
 */
function get_element_length($element) {
	try {
		
		// For path, line, polyline, polygon: use getTotalLength but with error handling
		if (typeof $element.getTotalLength === 'function') {
			try {
				const calculated_length = $element.getTotalLength();
				const svg = $element.closest('svg');
				const viewbox = svg.getAttribute('viewBox');
				
				if (viewbox) {
					// Parse viewBox to get the coordinate system dimensions
					const [
						viewbox_x,
						viewbox_y,
						viewbox_width,
						viewbox_height,
					] = viewbox.split(/\s+/).map(Number);
					
					// Get the actual rendered dimensions
					const rendered_width = svg.clientWidth;
					const rendered_height = svg.clientHeight;
					
					// Calculate the scale factor from viewBox to rendered size
					const scale_x = rendered_width / viewbox_width;
					const scale_y = rendered_height / viewbox_height;
					
					// Check ratio mode to determine which scale to use
					const ratio = svg.getAttribute('preserveAspectRatio') || 'xMidYMid meet';
					const is_slice_mode = ratio.includes('slice');
					
					// Use appropriate scale factor based on preserve aspect ratio mode
					// - slice mode: use larger scale (fills entire area, may crop)
					// - meet mode: use smaller scale (fits entirely within area)
					const scale = is_slice_mode
						? Math.max(scale_x, scale_y)
						: Math.min(scale_x, scale_y);
					
					// Scale the path length to match the visual rendering
					const scaled_length = calculated_length * scale;
					
					return scaled_length;
				}
				
				// Fallback if no viewBox
				return calculated_length;
			} catch (render_error) {
				// console.warn('getTotalLength failed:', render_error, $element);
				// If getTotalLength fails due to rendering issues, skip this element
				return 0;
			}
		}
		
		// For basic shapes, calculate perimeter manually
		const tag_name = $element.tagName.toLowerCase();
		
		if (tag_name === 'circle') {
			const r = parseFloat($element.getAttribute('r') || 0);
			return 2 * Math.PI * r;
		}
		
		if (tag_name === 'ellipse') {
			const rx = parseFloat($element.getAttribute('rx') || 0);
			const ry = parseFloat($element.getAttribute('ry') || 0);
			
			// Approximation for ellipse perimeter
			return Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
		}
		
		if (tag_name === 'rect') {
			const width = parseFloat($element.getAttribute('width') || 0);
			const height = parseFloat($element.getAttribute('height') || 0);
			return 2 * (width + height);
		}
		
		return 0;
	} catch (e) {
		console.warn('Could not calculate path length:', e, $element);
		return 0;
	}
}


/**
 * Animate the drawing of paths
 * @param {element} $icon
 */
function animate_paths($icon) {
	const $svg = $icon.querySelector(svg_$);
	if (!$svg) return;
	
	const delay = parseInt($icon.getAttribute(delay_attr));
	const max_duration = parseInt($icon.getAttribute(duration_attr));
	const is_proportional = $icon.getAttribute(proportional_attr) === g.true_s;
	const is_reverse_order = $icon.getAttribute(reverse_order_attr) === g.true_s;
	
	const $drawable_elements = get_drawable_elements($svg);
	
	// Track completed paths
	let completed_paths = 0;
	const total_paths = $drawable_elements.length;
	
	// Find longest path if proportional mode is enabled
	let max_length = 0;
	if (is_proportional) {
		max_length = $drawable_elements.reduce((max, $element) => {
			const length = parseFloat($element.style.getPropertyValue(path_length_prop)) || 0;
			return Math.max(max, length);
		}, 0);
	}
	
	// Set duration on each individual element
	$drawable_elements.forEach(($element, index) => {
		let element_duration = max_duration;
		
		if (is_proportional && max_length > 0) {
			const element_length = parseFloat($element.style.getPropertyValue(path_length_prop));
			element_duration = Math.max(draw_duration_min, (element_length / max_length) * max_duration);
		}
		
		$element.style.setProperty(duration_prop, `${element_duration}ms`);
		
		// Add transition end listener to track when this path completes
		const handle_path_complete = (event) => {
			if (event.propertyName !== stroke_dash_offset_prop) return false;
			
			$element.classList.remove(svg_drawing_c);
			$element.classList.add(svg_drawn_c);
			$element.removeEventListener(g.transition_end_e, handle_path_complete);
			completed_paths++;
			
			// If all paths are complete, mark icon as drawn
			if (completed_paths === total_paths) {
				$icon.classList.remove(icon_drawing_c);
				$icon.classList.add(icon_drawn_c);
				$icon.dispatchEvent(new CustomEvent(draw_complete_e));
			}
		};
		
		$element.addEventListener(g.transition_end_e, handle_path_complete);
		
		// Calculate delay index based on reverse order mode
		const delay_index = (is_reverse_order)
			? (total_paths - 1) - index
			: index;
		
		setTimeout(() => {
			$element.classList.add(svg_drawing_c);
		}, delay_index * delay);
	});
	
	// Mark icon as drawing after a short delay
	setTimeout(() => {
		$icon.classList.add(icon_drawing_c);
	}, 10);
}


/**
 * Get all drawable SVG elements within an SVG, excluding those in <defs>
 * @param {element} $svg
 * @return {Array}
 */
function get_drawable_elements($svg) {
	const $all_elements = $svg.querySelectorAll(drawable_elements_$);
	
	// Filter out elements that are inside definition containers
	const definition_tags = ['defs', 'symbol'];
	return Array.from($all_elements).filter($element => {
		let $parent = $element.parentElement;
		while ($parent && $parent !== $svg) {
			if (definition_tags.includes($parent.tagName.toLowerCase())) return false;
			
			$parent = $parent.parentElement;
		}
		return true;
	});
}
